import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import CommonSlider from '../../shared/elements/slider';
import { mobileDetectionForWidthLessThan768, mobileDetectionForWidthLessThanOrEqualTo768 } from "../../../utils/browser";
import i18n from '../../../utils/translations/i18n';
import { useGetPricesDetailsMutation } from '../../../store/pricing';
import { useReorderMutation } from '../../../store/reorder';
import { currencyFormatter } from '../../../utils/currency';
import store, { RootState } from "../../../store";
import { reOrderPayload } from "../../../models/reOrderPayload";
import { config } from '../../../utils/config';
import "./index.scss";
import Cart from '../../../models/cart';
import { getCart, newCartApi } from '../../../store/newCart';
import { setCurrentOperation, setIsReorderImpressionFired, setReorderProducts, setUserState, stopLoading } from '../../../store/application';
import { addReorderItemToCartGa, dispatchProdRecCarouselEvent, reorderImpressionEvent, updateGaCartView} from '../ga-module';
import { DispatchErrorsEvent } from '../../order-confirmation/ga-utils';
import constants  from "../../../utils/config/const.json";
import { CartOperation } from '../../../enums/cartOperation';

const {t} = i18n;
declare const window: any;

export function ProductCard({item, dataLength}) {
  const [getPricesDetails] = useGetPricesDetailsMutation();
  const [setReorder] = useReorderMutation();
  const [moqPrice, setMoqPrice] = useState('');
  const [hoqPrice, setHoqPrice] = useState('');
  const isMobileDevice = mobileDetectionForWidthLessThan768();
  const dispatch = useDispatch();
  const {
    applicationState: { user, reorderProducts,coupon,isShippingDisc },
  } = useSelector((state: RootState) => state);
  const locale = config('locale');

  const fetchPrice = async () => {
    try{
      const response: any = await getPricesDetails(item.productKey?.toLowerCase());
      const data = response?.data;
      if(data){
        let tiersArray =  data?.tiers;
        if(tiersArray.length > 0){
          setMoqPrice(currencyFormatter(tiersArray[0]?.tier?.unitPrice, data?.fractionDigits));
          setHoqPrice(currencyFormatter(tiersArray[tiersArray.length-1]?.tier?.unitPrice, data?.fractionDigits));
        }
      } else {
        throw new Error(response);
    }
    } catch(error) {
        console.error("Error while getting prices data", error);
    }
  };

  useEffect(() => {
    fetchPrice();
  }, [item.productKey]);

  const onClickHandler = async () => {
    try {
      const payload: reOrderPayload = {
        "orderID": item.orderId,
        "orderNumber": item.orderNumber,
        "regionCode": config('regionCode'),
        "cartID": user?.cartId,
        "lineItem": [
          {
            "lineItemId": item.lineItemId,
            "productKey": item.productKey,
            "productFamilyName": item.productFamilyName,
            "mcpSku": item.mcpSku,
            "imprintColor": item.imprintColor,
            "templateKey": item.templateKey,
            "UDS": item.UDS,
            "customizationInfo": item.customizationInfo,
            "digitalPrintProofRequired": item.digitalPrintProofRequired,
            "specialInstructionsForLineItem": item.specialInstructionsForLineItem,
            "lineitemStatus": item.lineitemStatus,
            "isV2Product": item?.isV2Product || false,
            ...(!item?.isV2Product ? {"variantID": item?.variantId || '',
              "variantSKU": item?.variantSku || ''
            } : {"merchantProductVersion" : item?.merchantProductVersion}),
            "quantity": item.quantity,
            "reviewAppProcessName": item.reviewAppProcessName,
            "IsLogoFree": item.customizationInfo?.imageInfo[0]?.imageUrl !== '' ? false : true,
            ...((item.isMultiSelectable) ? {
              "isMultiSelectable" : item.isMultiSelectable,
              "selectionAttributes": item.selectionAttributes || '',
              "subItem": item.subItem || [],
              "multiValueStr": item.multiValueStr ? item.multiValueStr : '',
            } : {}),
          }
        ],
        "userID": user?.userId,
        "isLoggedIn": user?.isLoggedIn === 'true' ? true : false,
        "locale": config('locale'),
        "lang": config('language'),
        "isCartPageReorder": true,
      }
      const resp: any = await setReorder({...payload});

      // remove product from slider if product is ineligible for reorder
      if(resp?.error?.data?.statusCode === 500 && resp?.error?.data?.error?.message === "Ineligible for reorder") {
        if(reorderProducts.length > 0) {
          const updatedProducts = reorderProducts.filter(product=> product.lineItemId !== item.lineItemId);
          dispatch(setReorderProducts(updatedProducts));
        }
      }

      if(resp?.error){
        if(resp?.error?.data?.statusCode === 500) {
          DispatchErrorsEvent(resp?.error?.data?.error?.message);
        } else {
          DispatchErrorsEvent(t('sign_up.somethingWrong'));
        }
      }

      let newAddedLineItemDataId = '';
      const cartObj = resp?.data?.data?.data?.attributes;

      if (user?.cartId) {
        const newAddedLineItems = cartObj?.newAddedLineItems?.filter(item => item?.type === 'mainItem');
        if (newAddedLineItems?.[0]?.id) {
          newAddedLineItemDataId = newAddedLineItems[0].id;
        }
      } else {
        window.initialUserState.cartId = cartObj.id;
        dispatch(setUserState({isLoggedIn : user.isLoggedIn,userId : user.userId, cartId: cartObj.id}))
        const lineItems = cartObj?.lineItems;
        if (lineItems?.[0]?.id) {
          newAddedLineItemDataId = lineItems[0].id;
        }
      }
      
      const reorderCart: any = new Cart(cartObj);
      if(reorderCart?.mainItemCount > 0 && reorderCart?.id) {
        const cartData = getCart(store.getState(), {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data;
        if(!cartData){
          dispatch(newCartApi.util.invalidateTags(['newCart']));
        } else {
          await dispatch(newCartApi.util.updateQueryData('getCartByCartId', {cartId: user?.cartId, userId: user?.userId, isLoggedIn: user?.isLoggedIn}, (draft) => Object.assign({}, reorderCart)));
        }
        window.pdi.overridePDI('[PR]{currPageName}_Reorder', -1, newAddedLineItemDataId);
        addReorderItemToCartGa(reorderCart, newAddedLineItemDataId);
        dispatch(setCurrentOperation({name : CartOperation.REORDER}))
        updateGaCartView(reorderCart,coupon,isShippingDisc);
      }
    }
    catch (err) {
      console.log('Error in reorder api', err);
    } finally {
      dispatch(stopLoading());
    }
  }

  return (
    <div className={isMobileDevice ? (dataLength === 1 ? 'product-card-desktop' : 'product-card-mobile') : 'product-card-desktop'}>
      <div className='img-container'>
        <img alt={item.productName} src={item.itemImage}></img>
      </div>
      <div className='description-container'>
        <div className='product_name'>{item.productName}</div>
        {constants.rightCurrencyLocaleList.includes(locale) ? 
          <div className='product_price'>{hoqPrice?.substring(0, hoqPrice.length - 1)} - {moqPrice}</div> :
          <div className='product_price'>{hoqPrice} - {moqPrice?.substring(1)}</div>
        }
        <div className='last-ordered-quantity'>{t('checkout.reorder.last_qty')} {item.quantity}</div>
        <div className='shipping-time'>{t('checkout.reorder.ships_within_msg')?.replace("[X]", item.shippingTAT)}*</div>
        <button className='add-to-cart-btn' onClick={onClickHandler} data-testid='add-to-cart-button'>{t('checkout.reorder.add_to_cart')}</button>
      </div>
    </div>
  );
};

const ProductReOrder = ({reorderableProducts, cart}) => {
  let previousSlide = 0;
  const isMobileDevice = mobileDetectionForWidthLessThanOrEqualTo768();
  const dispatch = useDispatch();

  const {
    applicationState: { isReorderImpressionFired },
  } = useSelector((state: RootState) => state);


  useEffect(() => {
    const reorderSlickSlider = document.querySelector('.reorder-slick-slider');

    const handleArrowClick = (event) => {
        const isNextArrow = event.target.closest('.slick-next');
        const isPrevArrow = event.target.closest('.slick-prev');

        const arrowButton = isNextArrow || isPrevArrow;

        if (arrowButton && !arrowButton.classList.contains('slick-disabled') && !arrowButton.classList.contains('click-disabled')) {
            const swipeDirection = isNextArrow ? 'right' : 'left';
            
            if (!isMobileDevice) {
                dispatchProdRecCarouselEvent(swipeDirection);
            }
        }
    };

    if (reorderSlickSlider) {
        reorderSlickSlider.addEventListener('click', handleArrowClick);
    }

    return () => {
        if (reorderSlickSlider) {
            reorderSlickSlider.removeEventListener('click', handleArrowClick);
        }
        dispatch(setIsReorderImpressionFired(false))
    };
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 2,
    afterChange: (currentSlide) => {
  
      if (previousSlide !== null) {
  
          if (isMobileDevice) {
              //ProdRecCarouselEvent GA event will fire on mobile swipe
              if (currentSlide !== previousSlide) {
                  dispatchProdRecCarouselEvent('mobile_scroll');
              }
          }
      }
  
      previousSlide = currentSlide;
  },
    responsive: [
      {
        breakpoint: 1285,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: (cart?.mainItemCount === 0 || !cart) ? 2 : 1,
          slidesToScroll: (cart?.mainItemCount === 0 || !cart) ? 2 : 1,
        }
      },
      {
        breakpoint: 430,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        }
      }
    ]
  };
  const productsToShow = reorderableProducts
  .filter(product => product.isPresentInCart === false || product.isPresentInCart === undefined)  // Filter out items where isPresentInCart is false
  .slice(0, 5); // Limit the result to the first 5 items

  useEffect(()=>{
    if(productsToShow.length > 0 && !isReorderImpressionFired){
      reorderImpressionEvent(productsToShow);
      dispatch(setIsReorderImpressionFired(true))
    }
  }, [productsToShow.length]);

  
  return productsToShow.length > 0 ? (
    <div
      className={`main-slider-container ${
        (cart?.mainItemCount === 0 || !cart) ? 'col-md-offset-2 full-width' : ''
      } ${productsToShow.length === 1 ? 'hide-arrows' : ''}`}
    >
      <h2 className='reorder-heading'>{t('checkout.reorder.buy_it_again')}</h2>
      <CommonSlider
        className={`reorder-slick-slider ${
          productsToShow.length <= 2 && window.innerWidth > 1024 ? 'full-size-card' : 'small-card'
        }`}
        settings={sliderSettings}
        data={productsToShow}
        RenderCard={ProductCard}
      />
    </div>
  ) : (
    <span></span>
  );
};

export default ProductReOrder;
